/**
 * Matching worksheet: numbered prompts on one side, lettered responses on the other.
 *
 * The responses are shuffled so that no correct response sits on the same row as its
 * prompt — a worksheet whose first answer is A and whose second is B teaches the
 * pattern, not the content. Extra distractor responses are mixed in at random; they
 * match nothing.
 */

import { randomIndex, shuffle } from "@/lib/tool-kit/classroom/random";

export const MAX_MATCHING_RESPONSES = 26;

export interface MatchingPair {
  term: string;
  definition: string;
}

export interface MatchingItem {
  number: number;
  text: string;
  /** The letter of the right response. */
  answer: string;
}

export interface MatchingResponse {
  letter: string;
  text: string;
}

export interface MatchingSheet {
  items: MatchingItem[];
  responses: MatchingResponse[];
  /** True when some responses match nothing, so the instructions should say so. */
  hasDistractors: boolean;
}

/** A, B, C … Z — the response column never runs past Z. */
export function responseLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

/** True when some prompt's correct response would sit on that prompt's own row. */
function alignsAnswer(order: readonly number[], pairCount: number): boolean {
  return order.some(
    (source, position) => source < pairCount && source === position
  );
}

/**
 * Build a worksheet from pairs and optional distractors.
 *
 * `swap` numbers the definitions and letters the terms — the layout test-writing
 * guides prefer, since students read each long prompt once and scan the short
 * response list many times.
 */
export function buildMatchingSheet(
  pairs: readonly MatchingPair[],
  distractors: readonly string[],
  { swap, random }: { swap: boolean; random: () => number }
): MatchingSheet {
  const prompts = pairs.map((pair) => (swap ? pair.definition : pair.term));
  const responses = [
    ...pairs.map((pair) => (swap ? pair.term : pair.definition)),
    ...distractors,
  ].slice(0, MAX_MATCHING_RESPONSES);
  const indices = responses.map((_, index) => index);

  let order = shuffle(indices, random);
  for (
    let attempt = 0;
    attempt < 200 && pairs.length > 1 && alignsAnswer(order, pairs.length);
    attempt += 1
  ) {
    order = shuffle(indices, random);
  }
  if (pairs.length > 1 && alignsAnswer(order, pairs.length)) {
    // Practically unreachable: rotate until nothing lines up.
    const offset = 1 + randomIndex(Math.max(1, responses.length - 1), random);
    order = indices.map((index) => (index + offset) % responses.length);
  }

  const letterOf = new Map<number, string>();
  for (const [position, source] of order.entries()) {
    letterOf.set(source, responseLetter(position));
  }
  return {
    items: prompts.map((text, index) => ({
      number: index + 1,
      text,
      answer: letterOf.get(index) ?? "",
    })),
    responses: order.map((source, position) => ({
      letter: responseLetter(position),
      text: responses[source] ?? "",
    })),
    hasDistractors: responses.length > pairs.length,
  };
}
